import { isAfter, isBefore, subMinutes } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import moment from "moment";

export function transformDate(originalDate: string | Date | undefined): string {
  if (!originalDate) {
    return "";
  }
  return new Date(originalDate)
    .toLocaleDateString("en-Us", {
      timeZone: "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .replace(/\//g, "-");
}

export function transformDateUTC(
  originalDate: string | Date | undefined
): string {
  if (!originalDate) {
    return "";
  }
  const date = new Date(originalDate);
  // UTC months return a number between 0 and 11, so we must add +1 to get the correct numerical month displayed
  const dateString = `${String(date.getUTCMonth() + 1).padStart(
    2,
    "0"
  )}-${String(date.getUTCDate()).padStart(2, "0")}-${date.getUTCFullYear()}`;

  return dateString;
}

export function transformDateWithTime(
  originalDate: string | undefined,
  is24Hr = false
): string {
  if (!originalDate) {
    return "";
  }
  const date = new Date(originalDate);
  const dateString = transformDateUTC(originalDate);

  return `${dateString}, ${getUtcTimeString(date, is24Hr)}`;
}

function getUtcTimeString(date: Date, is24Hr = false): string {
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");

  if (is24Hr) {
    const hour = date.getUTCHours();
    return `${hour}:${minutes}`;
  }
  const am = date.getUTCHours() < 12;
  const hour = am ? date.getUTCHours() : date.getUTCHours() - 12;
  return `${hour}:${minutes} ${am ? "AM" : "PM"}`;
}

// will display 24 hour minutes/seconds, but can be used for table sorting with seconds and fractional seconds
export function transformStringToTimeZoneAndFullTime(
  date: Date | string | undefined,
  timeZone: string | undefined,
  viewable = true
): string {
  return moment
    .tz(date?.toString() ?? "", timeZone ?? "America/New_York")
    .format(viewable ? "MM-DD-YYYY HH:mm" : "MM-DD-YYYY HH:mm:ss:SSS");
}

/**
 * A convenience method that will convert a string to a date only if it is defined.
 *
 * This avoid the issue where `new Date(undefined)` still returns a valid date.
 *
 * @param dateString The date string to convert
 * @returns The Date object of the string if defined, otherwise undefined
 */
export function convertStringToDate(dateString: string | Date | undefined) {
  if (!dateString) {
    return undefined;
  }
  return new Date(dateString);
}

// export function secondsToMMSS(seconds?: number): string {
//   const milliseconds = (seconds ?? 0) * 1000;
//   const date = new Date(milliseconds);
//   return `${date.getMinutes()} m ${date.getSeconds()} s`;
// }
export function secondsToMMSS(seconds?: number): string {
  const totalSeconds = seconds ?? 0;
  const minutes = Math.floor(totalSeconds / 60); // Calculate total minutes
  const remainingSeconds = totalSeconds % 60; // Calculate remaining seconds

  // Padding with zero if the remaining seconds are less than 10
  const formattedSeconds =
    remainingSeconds < 10 ? `0${remainingSeconds}` : remainingSeconds;

  if (minutes < 60) {
    return `${minutes} m ${formattedSeconds} s`;
  } else {
    const hours = Math.floor(minutes / 60); // Calculate total hours
    const remainingMinutes = minutes % 60; // Calculate remaining minutes

    return `${hours} h ${remainingMinutes} m ${formattedSeconds} s`;
  }
}

export function secondsToHMS(seconds?: number): string {
  const milliseconds = (seconds ?? 0) * 1000;

  const date = new Date(milliseconds);

  // Calculate any left over hours. This is important in case it goes over 24 hours.
  const hours = Math.floor((seconds ?? 0) / (60 * 60));

  return `${hours} h ${date.getUTCMinutes()} m ${date.getUTCSeconds()} s`;
}

export function isInTimeWindow(
  currentDate: Date,
  start?: Date,
  end?: Date,
  accountTimeZone?: string
): boolean {
  // Convert current time with server time zone to UTC
  const currentDateUtc = subMinutes(
    currentDate,
    currentDate.getTimezoneOffset()
  );

  const timeZone: string = accountTimeZone ?? "America/New_York";

  if (start) {
    const startDate = zonedTimeToUtc(start, timeZone);
    if (isBefore(currentDateUtc, startDate)) {
      return false;
    }
  }

  if (end) {
    const endDate = zonedTimeToUtc(end, timeZone);
    if (isAfter(currentDateUtc, endDate)) {
      return false;
    }
  }

  return true;
}

export const sortByDate = (a?: Date, b?: Date, ascending = true) => {
  if (!a || !b) {
    return 0;
  }

  const directionMultiplier = ascending ? 1 : -1;
  if (isBefore(a, b)) {
    return -1 * directionMultiplier;
  }
  if (isBefore(b, a)) {
    return 1 * directionMultiplier;
  }
  return 0;
};
